import React, { useState, useEffect } from "react";
import "./admin-course-reading.css";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import ASSETS from "../../../../app/config/assets";
import { FaArrowUpLong } from "react-icons/fa6";
import Button from "../../../../ui/atoms/buttons/Button";
import ChapterItem from "../../components/chapter-item/ChapterItem";
import TypewriterHTML from "../../../student/components/type-writter/TypewriterHTML";
import NotFound from "../../../../ui/organisms/not-found/404";
import Loading from "../../../../ui/atoms/loading/Loading";
import AdminLessonQuestionsSet from "./AdminLessonQuestionsSet";
import {
  capitalizeAll,
  formatDuration,
  isEmpty,
  slugify,
} from "../../../../core/utils/helpers";
import { useSelector } from "react-redux";
import {
  onGetChaptersByCourseId,
  onGetChaptersReleased,
  onGetChaptersArchived,
  onGetLessonsReleased,
  onGetLessonsArchived,
} from "../../services/course";
import useAxiosPrivate from "../../../../app/providers/context-provider/useAxiosPrivate";
import Swal from "sweetalert2";
import moment from "moment";
import { getMimeType } from "../../../../core/utils/common-validators";

const _updated_at = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");

const AdminCourseReading = () => {
  const axiosPrivate = useAxiosPrivate();
  const { course_id } = useParams();
  const [searchParams] = useSearchParams();
  const course_title = searchParams.get("view");
  const navigate = useNavigate();

  const [isToggleQuestionsSet, setIsToggleQuestionsSet] = useState(false);
  const [isToggleDiscussion, setIsToggleDiscussion] = useState(false);

  const [coursesData, setCoursesData] = useState({});

  const [forceOpenChapterId, setForceOpenChapterId] = useState(null);
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [currentReadingLesson, setCurrentReadingLesson] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);

  const [lessonStartTime, setLessonStartTime] = useState(null);
  const [canGoNext, setCanGoNext] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [currentLessonTiming, setCurrentLessonTiming] = useState(0);

  const currentCourse = useSelector(
    (state) => state?.schoolState?.currentCourse,
  );
  const _course_id = currentCourse?.course_id;
  const _course_title = currentCourse?.course_title;

  useEffect(() => {
    if (course_id !== _course_id || slugify(_course_title) !== course_title) {
      setCoursesData({});
    }
  }, [course_id, course_title]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchChapters = async () => {
      try {
        const response = await onGetChaptersByCourseId(axiosPrivate,
          { course_id: _course_id },
          signal,
        );
        const _data = response?.data;
        setCoursesData(_data);

        const firstChapter = _data?.result?.[0];
        const firstLesson = firstChapter?.lessons?.[0];

        if (firstChapter && firstLesson) {
          setSelectedChapterId(firstChapter.id);
          setSelectedLessonId(firstLesson.id);
          setCurrentReadingLesson(firstLesson);

          // ouvrir le chapitre automatiquement
          setForceOpenChapterId(firstChapter.id);

          // initialiser le timer
          initLessonTimer(firstLesson);

          // fonction centralisée de sélection
          selectLesson(firstChapter.id, firstLesson);
        }
      } catch (err) {
        if (err.name === "AbortError") return; // requête annulée
        console.error(err);
      }
    };

    fetchChapters();

    return () => {
      controller.abort();
    };
  }, [isRefresh]);

  const onToggleQuestionsSet = () => {
    setIsToggleQuestionsSet(!isToggleQuestionsSet);
  };

  const onToggleDiscussion = () => {
    setIsToggleDiscussion(!isToggleDiscussion);
  };

  useEffect(() => {
    if (!lessonStartTime || currentLessonTiming === 0) return;

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lessonStartTime) / 1000);

      const timeLeft = currentLessonTiming - elapsed;

      if (timeLeft <= 0) {
        setRemainingTime(0);
        setCanGoNext(true);
        clearInterval(interval);
      } else {
        setRemainingTime(timeLeft);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lessonStartTime, currentLessonTiming]);

  const chapters = coursesData?.result || [];
  const currentChapterIndex = chapters.findIndex(
    (c) => c.id === selectedChapterId,
  );
  const currentLessonIndex = chapters[currentChapterIndex]?.lessons?.findIndex(
    (l) => l.id === selectedLessonId,
  );
  const isFirstLesson = currentChapterIndex === 0 && currentLessonIndex === 0;

  const handleNext = () => {
    if (!canGoNext) return;

    const currentChapter = chapters[currentChapterIndex];
    const lessons = currentChapter.lessons;

    if (currentLessonIndex < lessons.length - 1) {
      const nextLesson = lessons[currentLessonIndex + 1];
      setSelectedLessonId(nextLesson.id);
      setCurrentReadingLesson(nextLesson);
      initLessonTimer(nextLesson);

      // Forcer l'ouverture du chapitre courant
      setForceOpenChapterId(currentChapter.id);
      return;
    }

    const nextChapter = chapters[currentChapterIndex + 1];
    if (nextChapter && nextChapter.lessons?.length > 0) {
      const firstLesson = nextChapter.lessons[0];
      setSelectedChapterId(nextChapter.id);
      setSelectedLessonId(firstLesson.id);
      setCurrentReadingLesson(firstLesson);
      initLessonTimer(firstLesson);

      // Forcer l'ouverture du chapitre suivant
      setForceOpenChapterId(nextChapter.id);
    }
  };

  const handlePrevious = () => {
    const currentChapter = chapters[currentChapterIndex];
    const lessons = currentChapter.lessons;

    // 1️⃣ lesson précédente dans le même chapitre
    if (currentLessonIndex > 0) {
      const prevLesson = lessons[currentLessonIndex - 1];
      setSelectedLessonId(prevLesson.id);
      setCurrentReadingLesson(prevLesson);
      initLessonTimer(prevLesson);

      // Forcer l'ouverture du chapitre courant
      setForceOpenChapterId(currentChapter.id);
      return;
    }

    // 2️⃣ passer au chapitre précédent
    const prevChapter = chapters[currentChapterIndex - 1];
    if (prevChapter && prevChapter.lessons?.length > 0) {
      const lastLesson = prevChapter.lessons[prevChapter.lessons.length - 1];
      setSelectedChapterId(prevChapter.id);
      setSelectedLessonId(lastLesson.id);
      setCurrentReadingLesson(lastLesson);
      initLessonTimer(lastLesson);

      // Forcer l'ouverture du chapitre précédent
      setForceOpenChapterId(prevChapter.id);
    }
  };

  const initLessonTimer = (lesson) => {
    const timingInSeconds = Number(lesson.timing) || 0;

    setCurrentLessonTiming(timingInSeconds);
    setLessonStartTime(Date.now());
    setRemainingTime(timingInSeconds);
    setCanGoNext(false);
  };

  const selectLesson = (chapterId, lesson) => {
    setSelectedChapterId(chapterId);
    setSelectedLessonId(lesson.id);
    setCurrentReadingLesson(lesson);

    initLessonTimer(lesson);
  };

  const onChaptersReleasing = async (is_action, chapter_id) => {
    Swal.fire({
      title: `Do you really want to ${
        is_action === "released" ? "Release" : "UnRelease"
      } the selected chapter ?`,
      text: "This action impact the whole chapter, and you are able to revert the action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${
        is_action === "released" ? "Released" : "UnRelease"
      }!`,
    }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action
        //
        Swal.fire({
          title: "Processing...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        // Simulate async operation (e.g., API call)
        onGetChaptersReleased(axiosPrivate, is_action, chapter_id, _updated_at)
          .then((response) => {
            if (response?.data?.status) {
              Swal.close();
              //
              setIsRefresh((prevState) => !prevState);
              //
              Swal.fire(
                `${is_action === "released" ? "Released" : "UnReleased"}!`,
                `The chapter has been ${
                  is_action === "released" ? "Released" : "UnReleased"
                }!`,
                "success",
              );
            }
          })
          .catch((error) => {
            Swal.close(); // close loading if error
            if (!error?.response) {
              Swal.fire("Error!", "No server response", "error");
            } else {
              Swal.fire(
                "Error!",
                error?.response?.data?.detail?.message,
                "error",
              );
            }
          });
      } else if (
        result.isDismissed ||
        result.dismiss === Swal.DismissReason.cancel
      ) {
        Swal.fire({
          title: "Cancelled",
          text: "The triggered action was cancelled!",
          icon: "error",
        });
      }
    });
  };

  const onChaptersArchiving = async (is_action, chapter_id) => {
    Swal.fire({
      title: `Do you really want to ${
        is_action === "archived" ? "Archive" : "UnArchive"
      } the chapter" ?`,
      text: "This action impact the whole chapter, and you are able to revert the action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${
        is_action === "archived" ? "Archive" : "UnArchive"
      }!`,
    }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action
        //
        Swal.fire({
          title: "Processing...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        // Simulate async operation (e.g., API call)
        onGetChaptersArchived(axiosPrivate, is_action, chapter_id, _updated_at)
          .then((response) => {
            if (response?.data?.status) {
              Swal.close();
              //
              setIsRefresh((prevState) => !prevState);
              //
              Swal.fire(
                `${is_action === "archived" ? "Archive" : "UnArchive"}!`,
                `The chapter has been ${
                  is_action === "archived" ? "Archived" : "UnArchived"
                }!`,
                "success",
              );
            }
          })
          .catch((error) => {
            Swal.close(); // close loading if error
            if (!error?.response) {
              Swal.fire("Error!", "No server response", "error");
            } else {
              Swal.fire(
                "Error!",
                error?.response?.data?.detail?.message,
                "error",
              );
            }
          });
      } else if (
        result.isDismissed ||
        result.dismiss === Swal.DismissReason.cancel
      ) {
        Swal.fire({
          title: "Cancelled",
          text: "The triggered action was cancelled!",
          icon: "error",
        });
      }
    });
  };

  const onLessonsReleasing = async (is_action, lesson_id) => {
    Swal.fire({
      title: `Do you really want to ${
        is_action === "released" ? "Release" : "UnRelease"
      } this lesson ?`,
      text: "This action impact the lesson's visibility, and you are able to revert the action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${
        is_action === "released" ? "Released" : "UnRelease"
      }!`,
    }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action
        //
        Swal.fire({
          title: "Processing...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        // Simulate async operation (e.g., API call)
        onGetLessonsReleased(axiosPrivate, is_action, lesson_id, _updated_at)
          .then((response) => {
            if (response?.data?.status) {
              Swal.close();
              //
              setIsRefresh((prevState) => !prevState);
              //
              Swal.fire(
                `${is_action === "released" ? "Released" : "UnReleased"}!`,
                `The lesson has been ${
                  is_action === "released" ? "Released" : "UnReleased"
                }!`,
                "success",
              );
            }
          })
          .catch((error) => {
            Swal.close(); // close loading if error
            if (!error?.response) {
              Swal.fire("Error!", "No server response", "error");
            } else {
              Swal.fire(
                "Error!",
                error?.response?.data?.detail?.message,
                "error",
              );
            }
          });
      } else if (
        result.isDismissed ||
        result.dismiss === Swal.DismissReason.cancel
      ) {
        Swal.fire({
          title: "Cancelled",
          text: "The triggered action was cancelled!",
          icon: "error",
        });
      }
    });
  };

  const onLessonsArchiving = async (is_action, lesson_id) => {
    Swal.fire({
      title: `Do you really want to ${
        is_action === "archived" ? "Archive" : "UnArchive"
      } the chapter" ?`,
      text: "This action impact the lesson's visibility, and you are able to revert the action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${
        is_action === "archived" ? "Archive" : "UnArchive"
      }!`,
    }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action
        //
        Swal.fire({
          title: "Processing...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        // Simulate async operation (e.g., API call)
        onGetLessonsArchived(axiosPrivate, is_action, lesson_id, _updated_at)
          .then((response) => {
            if (response?.data?.status) {
              Swal.close();
              //
              setIsRefresh((prevState) => !prevState);
              //
              Swal.fire(
                `${is_action === "archived" ? "Archive" : "UnArchive"}!`,
                `The lesson has been ${
                  is_action === "archived" ? "Archived" : "UnArchived"
                }!`,
                "success",
              );
            }
          })
          .catch((error) => {
            Swal.close(); // close loading if error
            if (!error?.response) {
              Swal.fire("Error!", "No server response", "error");
            } else {
              Swal.fire(
                "Error!",
                error?.response?.data?.detail?.message,
                "error",
              );
            }
          });
      } else if (
        result.isDismissed ||
        result.dismiss === Swal.DismissReason.cancel
      ) {
        Swal.fire({
          title: "Cancelled",
          text: "The triggered action was cancelled!",
          icon: "error",
        });
      }
    });
  };

  return (
    <React.Fragment>
      <div className="admin-course-reading">
        <div className="a-c-r-tile tile-1">
          <div className="tile-1-head">
            <div className="w-full d-flex justify-space-between">
              <Button
                type={"button"}
                preferedStyle={"default"}
                title={"⬅️ Back"}
                action={() => navigate(-1)}
              />
              <Button
                type={"button"}
                preferedStyle={"default"}
                title={"📖 New Chapter"}
                action={() =>
                  navigate(
                    `/admin/schools/streams/courses/${_course_id}/chapters/create`,
                  )
                }
              />
            </div>
            <h1>{capitalizeAll(_course_title)}</h1>
          </div>
          <div className="tile-1-content">
            {isEmpty(coursesData?.result) ? (
              <NotFound
                type={"data"}
                title={"No Chapter found!"}
                text={""}
                isBoutton={false}
              />
            ) : (
              coursesData?.result?.map((chapter) => (
                <ChapterItem
                  key={chapter.id}
                  chapter={chapter}
                  selectedChapterId={selectedChapterId}
                  selectedLessonId={selectedLessonId}
                  onSelectLesson={(chapterId, lesson) => {
                    setSelectedChapterId(chapterId);
                    setSelectedLessonId(lesson.id);
                    setCurrentReadingLesson(lesson);
                    initLessonTimer(lesson); // synchronise le timer

                    // forcer l'ouverture du chapitre correspondant
                    setForceOpenChapterId(chapterId);
                  }}
                  onSelectChapter={(chapter) => {
                    if (chapter.lessons?.length > 0) {
                      const firstLesson = chapter.lessons[0];
                      setSelectedChapterId(chapter.id);
                      setSelectedLessonId(firstLesson.id);
                      setCurrentReadingLesson(firstLesson.lessons_sections);
                      initLessonTimer(firstLesson); // synchronise le timer

                      // forcer l'ouverture du chapitre
                      setForceOpenChapterId(chapter.id);
                    }
                  }}
                  forceOpenChapterId={forceOpenChapterId}
                  onRelease={() =>
                    onChaptersReleasing(
                      chapter?.is_activated ? "unreleased" : "released",
                      chapter.id,
                    )
                  }
                  onArchive={() =>
                    onChaptersArchiving(
                      chapter?.is_deleted ? "unarchived" : "archived",
                      chapter.id,
                    )
                  }
                />
              ))
            )}
          </div>
        </div>
        <div className="a-c-r-tile tile-2">
          {isLoading ? (
            <div className="w-full h-full d-flex align-center justify-center">
              <Loading type={"type-2"} />
            </div>
          ) : (
            <>
              <div className="tile-2-head">
                <div className="w-fit-content">
                  <Button
                    type={"button"}
                    preferedStyle={"normal"}
                    title={"Lesson Questions Set"}
                    action={() => onToggleQuestionsSet()}
                  />
                </div>
                <div className="w-fit-content">
                  <Button
                    type={"button"}
                    preferedStyle={`${currentReadingLesson?.is_activated ? "normal" : "default"}`}
                    title={`Lesson ${currentReadingLesson?.is_activated ? "Released" : "UnReleased"}, click to ${currentReadingLesson?.is_activated ? "UnRelease" : "Release"} it`}
                    action={() =>
                      onLessonsReleasing(
                        currentReadingLesson?.is_activated
                          ? "unreleased"
                          : "released",
                        currentReadingLesson?.id,
                      )
                    }
                  />
                </div>
                <div className="w-fit-content">
                  <Button
                    type={"button"}
                    preferedStyle={`${currentReadingLesson?.is_deleted ? "normal" : "default"}`}
                    title={`Lesson ${currentReadingLesson?.is_deleted ? "Archived" : "UnArchived"}, click to ${currentReadingLesson?.is_deleted ? "UnArchive" : "Archive"} it`}
                    action={() =>
                      onLessonsArchiving(
                        currentReadingLesson?.is_deleted
                          ? "unarchived"
                          : "archived",
                        currentReadingLesson?.id,
                      )
                    }
                  />
                </div>
                <div className="w-fit-content">
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"💬 Talk to My Assistant"}
                    action={() => onToggleDiscussion()}
                  />
                </div>
              </div>
              <div className="tile-2-content">
                {currentReadingLesson?.lessons_sections?.map(
                  (lesson_section, ls_idx) => {
                    const groupedAssets =
                      lesson_section?.lesson_sections_assets?.reduce(
                        (acc, item) => {
                          acc[item.type] = acc[item.type] || [];
                          acc[item.type].push(item);
                          return acc;
                        },
                        {},
                      );
                    const assets_audios = groupedAssets.audio || [];
                    const assets_videos = groupedAssets.video || [];
                    const assets_images = groupedAssets.image || [];
                    return (
                      <>
                        {/* <div
                          className="ql-editor"
                          dangerouslySetInnerHTML={{
                            __html: lesson_section?.description,
                          }}
                          key={ls_idx}
                        /> */}
                        <TypewriterHTML
                          key={ls_idx}
                          html={lesson_section?.description}
                        />

                        {assets_audios?.length > 0 && (
                          <div className="tile-2-content-audios">
                            {assets_audios?.map((audio, a_k) => {
                              return (
                                <div className="content-audios-item" key={a_k}>
                                  <audio
                                    controls
                                    controlsList="nodownload"
                                    onContextMenu={(e) => e.preventDefault()}
                                  >
                                    <source
                                      src={`${process.env.REACT_APP_API_SERVER_URL}:${process.env.REACT_APP_API_SERVER_PORT}/storage/audios/lessons/${audio?.title}`}
                                      type={getMimeType(audio?.title)}
                                    />
                                  </audio>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {assets_videos?.length > 0 && (
                          <div className="tile-2-content-videos">
                            {assets_videos?.map((video, v_k) => {
                              return (
                                <div className="content-videos-item" key={v_k}>
                                  <video
                                    controls
                                    width={"100%"}
                                    controlsList="nodownload"
                                    onContextMenu={(e) => e.preventDefault()}
                                  >
                                    <source
                                      src={`${process.env.REACT_APP_API_SERVER_URL}:${process.env.REACT_APP_API_SERVER_PORT}/storage/videos/lessons/${video?.title}`}
                                      type={getMimeType(video?.title)}
                                    />
                                    Your browser does not support the video
                                    element.
                                  </video>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {assets_images?.length > 0 && (
                          <div className="tile-2-content-images">
                            {assets_images?.map((image, i_k) => {
                              return (
                                <div className="content-images-item" key={i_k}>
                                  <img
                                    src={`${process.env.REACT_APP_API_SERVER_URL}:${process.env.REACT_APP_API_SERVER_PORT}/storage/images/lessons/${image?.title}`}
                                    alt={`${image?.title}`}
                                    controlsList="nodownload"
                                    onContextMenu={(e) => e.preventDefault()}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  },
                )}
              </div>
              <div className="tile-2-foot">
                <div className="w-20">
                  <Button
                    type="button"
                    preferedStyle="default"
                    title="⬅️ Previous"
                    action={handlePrevious}
                    disabled={isFirstLesson}
                  />
                </div>
                <div className="w-20">
                  <Button
                    type="button"
                    preferedStyle="default"
                    title={
                      canGoNext
                        ? "Next ➡️"
                        : `⏳ ${formatDuration(remainingTime)} remain`
                    }
                    action={handleNext}
                    disabled={!canGoNext}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Questions Set */}
      <div
        className={
          isToggleQuestionsSet
            ? "questions-set-stage slide-in"
            : "questions-set-stage slide-out"
        }
      >
        <div className="stage-head w-full">
          <h1>Lesson Questions Set</h1>
          <Button
            type={"button"}
            preferedStyle={"default"}
            title={"Close Questions Set"}
            action={() => onToggleQuestionsSet()}
          />
        </div>
        <div className="stage-container">
          {isToggleQuestionsSet && (
            <AdminLessonQuestionsSet lesson_id={currentReadingLesson?.id} />
          )}
        </div>
      </div>
      {/* Discussion */}
      <div
        className={
          isToggleDiscussion
            ? "reading-discussion-stage slide-in"
            : "reading-discussion-stage slide-out"
        }
      >
        <div className="stage-head w-full">
          <div className="assisstant">
            <img src={ASSETS?.icon_assistance_png} alt="my ai assisstant" />
            <div className="d-flex flex-col">
              <h1>Hello, I am your Assisstant.</h1>
              <p>Tell me how can i hell you today ?</p>
            </div>
          </div>
          <Button
            type={"button"}
            preferedStyle={"default"}
            title={"💬 Close My Assistant"}
            action={() => onToggleDiscussion()}
          />
        </div>
        <div className="stage-container">
          <div className="ai-response">
            Below is a professional assessment set of 20 questions for the
            course Introduction to Management, specifically for the chapter
            “Introduction to Management.” The questions mix knowledge,
            comprehension, analysis, and applied thinking, and measure both
            cognitive and psychological skills such as decision-making, critical
            thinking, leadership awareness, and organizational understanding.
          </div>
          <div className="user-question">
            Below is a professional assessment set of 20 questions for the
            course Introduction to Management, specifically for the chapter
            “Introduction to Management.” The questions mix knowledge,
            comprehension, analysis, and applied thinking, and measure both
            cognitive and psychological skills such as decision-making, critical
            thinking, leadership awareness, and organizational understanding.
          </div>
        </div>
        <div className="stage-foot">
          <textarea placeholder="Type your question"></textarea>
          <span>
            <FaArrowUpLong className="icon" />
          </span>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AdminCourseReading;
